/**
 * ProjectBaseBranchSection - which branch this project's shared environments
 * and pull-request previews are built from.
 *
 * Not the same fact as `repositoryDefaultBranch`, and deliberately a separate
 * field: the default branch says where a new piece of work *starts*, this one
 * says what QA is supposed to look like. In the simplest setups they are the
 * same string, which is exactly why one field doing both jobs would be wrong
 * the first time a team ships from `qa-main` and branches from `main`.
 *
 * Null until somebody names one. Nothing guesses: an environment built from the
 * wrong branch is a QA that tests code nobody asked about, and the failure is
 * silent — so «Nuevo entorno del proyecto» stays disabled and says why instead.
 *
 * Editing is `p.edit` on the project. The inline warning is best effort: it
 * needs `p.env.use` to read the environments, and somebody who may edit the
 * project but not use its environments simply does not get it.
 */
import type { ProjectId } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useHasProjectPermission } from "../../auth/authStore";
import { type AdminEnvironmentRow, EnvironmentsClient } from "../../auth/EnvironmentsClient";
import { gitBranchesQueryOptions } from "../../lib/gitReactQuery";
import { newCommandId } from "../../lib/utils";
import { readNativeApi } from "../../nativeApi";
import { useStore } from "../../store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/** Branch names as a person types them: `origin/qa-main` is `qa-main`. */
const withoutRemotePrefix = (name: string): string => name.replace(/^origin\//, "");

export function ProjectBaseBranchSection(props: { readonly projectId: ProjectId }) {
  const { projectId } = props;
  const project = useStore((state) => state.projects.find((entry) => entry.id === projectId));
  const canEdit = useHasProjectPermission(projectId, "p.edit");
  const stored = project?.baseBranch ?? null;

  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [environments, setEnvironments] = useState<ReadonlyArray<AdminEnvironmentRow>>([]);

  // Server state is the default view; a local edit shadows it until it lands.
  useEffect(() => {
    setDraft(null);
  }, [projectId, stored]);

  const branches = useQuery({
    ...gitBranchesQueryOptions(project?.cwd ?? null),
    // The list decorates an input that also accepts anything typed; a failure
    // to read it is not worth an error on the screen.
    retry: false,
  });

  useEffect(() => {
    let cancelled = false;
    void EnvironmentsClient.listUsable(projectId)
      .then((rows) => {
        if (!cancelled) setEnvironments(rows);
      })
      .catch(() => {
        if (!cancelled) setEnvironments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!canEdit) return null;

  const value = draft ?? stored ?? "";
  const listId = `base-branch-options-${projectId}`;
  const suggestions = Array.from(
    new Set((branches.data?.branches ?? []).map((branch) => withoutRemotePrefix(branch.name))),
  ).toSorted((left, right) => left.localeCompare(right));

  // Environments following something else are the reason this field is not a
  // free-text note: retracking is not automatic, so changing the branch here
  // leaves them where they were, and silence would read as "done".
  const straggling = environments.filter(
    (row) =>
      row.kind === "project" &&
      row.deletedAt === null &&
      row.trackedBranch !== null &&
      row.trackedBranch !== (stored ?? ""),
  );

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const api = readNativeApi();
    if (!api) {
      setError("No hay conexión con el servidor.");
      return;
    }
    const next = value.trim();
    setSaving(true);
    setError(null);
    try {
      await api.orchestration.dispatchCommand({
        type: "project.meta.update",
        commandId: newCommandId(),
        projectId,
        baseBranch: next.length === 0 ? null : next,
      });
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la rama base.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-5">
      <div className="min-w-0">
        <h4 className="text-[15px] font-semibold tracking-tight">Rama base</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          De aquí nacen los entornos de proyecto y las previews de pull request.
        </p>
      </div>

      <form onSubmit={save} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`base-branch-${projectId}`}>Rama</Label>
          <Input
            id={`base-branch-${projectId}`}
            list={listId}
            value={value}
            disabled={saving}
            placeholder={project?.repositoryDefaultBranch ?? "main"}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
          />
          <datalist id={listId}>
            {suggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <p className="text-[11px] text-muted-foreground">
            Vacío para quitarla. Sin rama base no se pueden crear entornos del proyecto.
          </p>
        </div>

        {straggling.length > 0 ? (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {straggling.length === 1 ? "Hay un entorno siguiendo" : "Hay entornos siguiendo"} otra
            rama:{" "}
            {straggling
              .map((row) => `${row.name ?? row.branch} → ${row.trackedBranch ?? ""}`)
              .join(", ")}
            . Cambiar esto no los mueve: seguirán actualizándose contra la suya.
          </div>
        ) : null}

        {error !== null ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" size="sm" disabled={saving || (draft === null && stored !== null)}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
          {draft !== null ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Descartar
            </Button>
          ) : null}
        </div>
      </form>
    </section>
  );
}
